package com.zi.support.shared

/** Issues tab app/company filters, shared so web, Android and iOS group incidents the same way. */
object IncidentFilters {
    /** App filter options, in menu order. */
    val apps: List<String> = listOf(
        "Central Driver", "Driver Hub", "Operations App", "DepoFlow", "DepoFlowA", "KPH Driver", "GT Driver",
        "Transport Driver", "Delta Driver", "GT Depot Portal", "Heating App",
    )

    /**
     * Driver apps report "HTTP 404 … systemdefaults/230 was not found" against servers that don't have that setting.
     * Those are noise everywhere except Gantrans production (gantrans.net.za) and its test server (port 56310), so
     * they're hidden on every Issues screen and get no push (functions isIgnoredSystemDefaults404). [text] is any dump
     * of the whole incident (all field values), so the server URL is found wherever the app put it. Any sign of
     * KPH, Delta, Texas or Transport hides it even if Gantrans is also mentioned; "system defaults" spellings all match.
     */
    fun isIgnoredSystemDefaults404(text: String): Boolean {
        val t = text.lowercase()
        val letters = t.filter { it.isLetterOrDigit() }
        if ("404" !in t || "systemdefault" !in letters) return false
        val gantrans = "gantrans.net.za" in t || ":56310" in t
        return !gantrans || listOf("kph", "delta", "texas", "transportdbn").any { it in letters }
    }

    /** The app filter an incident's `appName` belongs to; unknown apps keep their own name. */
    fun appCategory(appName: String): String {
        val key = appName.lowercase().filter { it.isLetterOrDigit() }
        return when {
            key.startsWith("centraldriver") -> "Central Driver"
            key.startsWith("driverhub") -> "Driver Hub"
            key.startsWith("operations") -> "Operations App"
            key == "depoflow" -> "DepoFlow"
            key == "depoflowa" -> "DepoFlowA"
            key == "kphdriver" -> "KPH Driver"
            key == "gantransdriver" || key == "gtdriver" -> "GT Driver"
            key == "transportdbndriver" || key == "transportdriver" -> "Transport Driver"
            key == "deltadriver" -> "Delta Driver"
            key == "gtdepotportal" -> "GT Depot Portal"
            key == "boilertracker" || key == "heatingapp" || key == "heating" -> "Heating App"
            else -> appName
        }
    }

    /** Company an incident belongs to: its own `company` field, else inferred from the app name and variant. Blank when unknown. */
    fun company(appName: String, variant: String, company: String): String {
        if (company.isNotBlank()) return company.trim()
        val app = appName.lowercase().filter { it.isLetterOrDigit() }
        val value = variant.lowercase()
        return when {
            app == "kphdriver" -> "KPH"
            app in setOf("gantransdriver", "gtdriver", "gtdepotportal", "boilertracker", "heatingapp") -> "Gantrans"
            app in setOf("transportdbndriver", "transportdriver") -> "Transport"
            app in setOf("deltadriver", "depoflow") -> "Delta"
            "gantrans" in value -> "Gantrans"
            "kph" in value -> "KPH"
            "transport" in value || "dbn" in value -> "Transport"
            "delta" in value -> "Delta"
            else -> ""
        }
    }

    /** `companies/{companyId}` document id for a company name, so "Gantrans Transport" and "GT" both land on "gantrans". */
    fun companyId(name: String): String {
        CompanyDirectory.savedId(name)?.let { return it }
        val slug = name.trim().lowercase().replace("&", " and ").replace(Regex("[^a-z0-9]+"), "-").trim('-')
        return when {
            "gantrans" in slug || slug == "gt" -> "gantrans"
            "kph" in slug -> "kph"
            "tyetrans" in slug || "tye-trans" in slug -> "tyetrans"
            "transport" in slug -> "transport"
            "delta" in slug -> "delta"
            slug == "fcl" -> "fcl"
            "avemel" in slug -> "avemel"
            "bees" in slug && ("cartridge" in slug || "cartidge" in slug) -> "bees-cartridge"
            "frenz" in slug -> "frenz-logistics"
            "rungatrans" in slug -> "rungatrans"
            slug == "sts" -> "sts"
            "texas" in slug -> "texas"
            "xmoor" in slug -> "xmoor"
            slug == "zi" -> "zi"
            else -> slug.ifBlank { "unassigned" }.take(80)
        }
    }
}
